// Contenido de ayuda contextual. Cada sección del cliente tiene un
// botón `?` que abre un modal con esta info: para qué sirve la
// sección + qué puede hacer ahí + opcionalmente un link a un
// tutorial en video.
//
// Filosofía: el cliente es low-tech. Explicaciones cortas, en
// segunda persona, sin jerga. "Aquí ves X. Para Y, toca Z."
//
// Agregar ayuda a una sección nueva:
//   1. Agrega una entrada aquí con un id estable
//   2. Pasa <SectionHeader helpId="ese-id" ... />
//
// tutorialId (opcional) conecta con el catálogo de tutoriales
// (src/lib/tutorials.ts) — si está, el modal muestra un botón
// "Ver tutorial" que abre el deep-link.

export interface HelpEntry {
  /** Título del modal (suele coincidir con el de la sección). */
  title: string;
  /** Cuerpo. 2-4 frases máximo. Segunda persona, tono iftmx. */
  body: string;
  /** Bullets opcionales de "qué puedes hacer aquí". */
  actions?: string[];
  /** Recordatorios destacados (caja "⚠️ Importante"). Para acciones
   *  que el coach NECESITA que el cliente haga (registrar, compartir). */
  emphasis?: string[];
  /** Si existe, el modal muestra "Ver tutorial" → /soporte?tutorial=id */
  tutorialId?: string;
}

export const HELP_CONTENT: Record<string, HelpEntry> = {
  dashboard: {
    title: 'Tu resumen del día',
    body: 'Es tu vista rápida de hoy: rutina asignada, comidas registradas y calorías. Lo primero que ves al abrir la app.',
    actions: [
      'Revisa qué te toca entrenar hoy',
      'Mira cuánto llevas de tus calorías',
      'Atiende las alertas si las hay',
    ],
  },
  rutina: {
    title: 'Tu rutina',
    body: 'El plan de ejercicios que tu coach te asignó para hoy. Marca cada serie conforme la haces y guarda el peso que levantaste para llevar control de tu progreso.',
    actions: [
      'Toca un ejercicio para ver cómo se hace',
      'Anota el peso y las repeticiones de cada serie',
      'Marca la rutina como completada al terminar',
    ],
    emphasis: [
      'Al terminar, registra tus ejercicios. Es lo que tu coach revisa para ajustar tu plan — si no lo registras, es como si no hubieras entrenado.',
      'Comparte tu logro con el equipo Insane Force Team por WhatsApp 💪 — motiva a todos y tu coach lo ve.',
    ],
  },
  cardio: {
    title: 'Cardio',
    body: 'Aquí registras tu cardio del día (caminata, bici, escaladora, lo que sea) para cumplir tu meta de quema de calorías.',
    actions: [
      'Registra el tipo de cardio y cuánto tiempo lo hiciste',
      'Mira si ya cumpliste tu meta diaria de quema',
    ],
    emphasis: [
      'Al terminar tu cardio, compártelo con el equipo Insane Force Team por WhatsApp con el botón de compartir. Así tu coach lleva tu seguimiento.',
    ],
  },
  nutricion: {
    title: 'Nutrición y calorías',
    body: 'Tu balance de energía del día: cuántas calorías llevas comidas contra tu meta. Registra lo que comes con texto o tomándole foto — la IA estima las calorías por ti.',
    actions: [
      'Registra una comida escribiéndola o con foto',
      'Revisa cuántas calorías y proteína te faltan',
      'Mira tu historial de comidas',
    ],
    // tutorialId: 'registrar-comida' — reactivar cuando exista el
    // video Tier 1 de registrar comida.
  },
  'plan-alimenticio': {
    title: 'Tu plan alimenticio',
    body: 'Las comidas que tu coach diseñó para ti. Marca las que ya hiciste para llevar control. Si no sabes qué comprar, abre la lista de súper y te decimos exactamente qué necesitas.',
    actions: [
      'Marca cada comida cuando la cumplas',
      'Ajusta la hora a la que comes cada cosa',
      'Genera tu lista de súper de la semana',
    ],
    // tutorialId: 'plan-alimenticio' — reactivar cuando exista el
    // video Tier 1 del plan alimenticio.
  },
  'fotos-progreso': {
    title: 'Fotos de progreso',
    body: 'Sube tus fotos una vez al mes (frente, espalda, lados). Compararlas mes a mes es la forma más real de notar tu transformación — la báscula a veces miente, el espejo no.',
    actions: [
      'Sube tu primera foto para tener punto de partida',
      'Repite cada mes para ver tu avance',
      'Compara fotos de distintos meses lado a lado',
    ],
  },
  evaluacion: {
    title: 'Evaluación corporal',
    body: 'Aquí va tu InBody o bioimpedancia. Tómale foto a tu resultado y la IA extrae tus números (grasa, músculo, agua) automáticamente para llevar tu evolución.',
    actions: [
      'Toma foto de tu reporte InBody y súbela',
      'Mira tu evolución de grasa y músculo en el tiempo',
    ],
  },
  marco: {
    title: 'Marco, tu asistente IA',
    body: 'Un asistente que responde tus dudas de nutrición y entrenamiento al instante, con el contexto de tu plan. Pregúntale lo que sea: "¿puedo cambiar el arroz por papa?", "¿qué hago si me duele la rodilla?".',
    actions: [
      'Pregunta dudas de tu rutina o tu dieta',
      'Pide alternativas a un alimento o ejercicio',
    ],
  },
  suplementos: {
    title: 'Tus suplementos',
    body: 'El protocolo de suplementos que tu coach te recomendó: qué tomar, cuánto y cuándo. Síguelo tal cual para sacarle el máximo a tu plan.',
    actions: ['Revisa qué suplemento toca y a qué hora'],
  },
  'mi-cuenta': {
    title: 'Tu cuenta y pagos',
    body: 'Aquí ves el estado de tu mensualidad con tu coach: si estás al corriente, cuándo vence y tu historial de pagos.',
    actions: ['Revisa tu estado de pago y próxima fecha'],
  },
};
